import { GameState } from "../state";
import { dist, len } from "../../core/math";
import { makeEnemy } from "./spawner";
import type { Enemy } from "../entities";
import { maxIntegrity } from "../stats";

// Fraction of an enemy's contact damage that nodes actually take per second.
// Slows node loss so the objective is defensible (readability + fairness pass).
const NODE_DAMAGE_FACTOR = 0.35;

// Enemy behavior + integration + contact damage to player and nodes.
export function updateEnemies(s: GameState, dt: number): void {
  for (const e of s.enemies) {
    if (!e.alive) continue;
    if (e.hitFlash > 0) e.hitFlash -= dt;
    if (e.buffed > 0) e.buffed -= dt;

    const speedMul = e.buffed > 0 ? 1.3 : 1;
    steer(s, e, dt, speedMul);

    // Friction on knockback, then integrate.
    e.vx *= 0.86;
    e.vy *= 0.86;
    e.x += e.vx * dt;
    e.y += e.vy * dt;

    // Keep inside arena.
    e.x = Math.max(8, Math.min(s.arenaW - 8, e.x));
    e.y = Math.max(8, Math.min(s.arenaH - 8, e.y));

    contact(s, e, dt);
  }

  // Corruptors buff nearby enemies (resolved after movement).
  for (const c of s.enemies) {
    if (!c.alive || c.def.behavior !== "buff") continue;
    for (const e of s.enemies) {
      if (e === c || !e.alive) continue;
      if (dist(c.x, c.y, e.x, e.y) < 160) e.buffed = 0.6;
    }
  }

  // Compact the array occasionally so it doesn't grow unbounded.
  if (s.enemies.length > 1200) s.enemies = s.enemies.filter((e) => e.alive);
}

function steer(s: GameState, e: Enemy, dt: number, speedMul: number): void {
  const p = s.player;
  switch (e.def.behavior) {
    case "rush":
    case "seek_player":
    case "boss": {
      // Boss: slow relentless approach; its attacks are run by the boss system.
      moveToward(e, p.x, p.y, e.speed * speedMul, dt);
      break;
    }
    case "seek_node": {
      const node = s.nearestOnlineNode(e.x, e.y);
      const tx = node ? node.x : p.x;
      const ty = node ? node.y : p.y;
      // If close to the player, prefer attacking PCKT.
      if (dist(e.x, e.y, p.x, p.y) < 90) moveToward(e, p.x, p.y, e.speed * speedMul, dt);
      else moveToward(e, tx, ty, e.speed * speedMul, dt);
      break;
    }
    case "buff":
    case "field": {
      // Hang at mid range from the player.
      const d = dist(e.x, e.y, p.x, p.y);
      const want = e.def.behavior === "buff" ? 240 : 150;
      const sign = d > want ? 1 : -0.5;
      moveToward(e, p.x, p.y, e.speed * speedMul * sign, dt);
      break;
    }
    case "steal": {
      if (e.carrying > 0) {
        // Flee with the loot toward arena edge.
        moveToward(e, e.x * 2 - p.x, e.y * 2 - p.y, e.speed * speedMul, dt);
      } else {
        // Make for the nearest fragment, else the player.
        const frag = nearestFragment(s, e.x, e.y);
        if (frag) moveToward(e, frag.x, frag.y, e.speed * speedMul, dt);
        else moveToward(e, p.x, p.y, e.speed * speedMul, dt);
      }
      break;
    }
    case "teleport": {
      e.cooldown -= dt;
      if (e.cooldown <= 0) {
        e.cooldown = s.rng.range(2.5, 4.5);
        // Blink near the player and (occasionally) clone.
        const a = s.rng.angle();
        const r = s.rng.range(120, 240);
        s.ring(e.x, e.y, e.def.color, 10, 180);
        e.x = Math.max(8, Math.min(s.arenaW - 8, p.x + Math.cos(a) * r));
        e.y = Math.max(8, Math.min(s.arenaH - 8, p.y + Math.sin(a) * r));
        s.ring(e.x, e.y, e.def.color, 10, 180);
        if (e.hp > e.maxHp * 0.5 && s.rng.chance(0.35) && s.enemies.length < 800) {
          const clone = makeEnemy(e.def, e.x, e.y, e.maxHp / e.def.hp);
          clone.hp = e.maxHp * 0.5;
          e.hp *= 0.5;
          s.enemies.push(clone);
        }
      } else {
        moveToward(e, p.x, p.y, e.speed * 0.4 * speedMul, dt);
      }
      break;
    }
  }
}

function moveToward(e: Enemy, tx: number, ty: number, speed: number, dt: number): void {
  const dx = tx - e.x;
  const dy = ty - e.y;
  const m = len(dx, dy) || 1;
  e.x += (dx / m) * speed * dt;
  e.y += (dy / m) * speed * dt;
}

function contact(s: GameState, e: Enemy, dt: number): void {
  const p = s.player;
  const dmgMul = (e.buffed > 0 ? 1.4 : 1) * s.difficulty.enemyDamage;

  // Steal fragments from the world on contact.
  if (e.def.behavior === "steal" && e.carrying === 0) {
    const frag = nearestFragment(s, e.x, e.y);
    if (frag && dist(e.x, e.y, frag.x, frag.y) < e.radius + 12) {
      e.carrying += frag.value;
      frag.alive = false;
    }
  }

  // Damage nodes the enemy overlaps. Decay is slowed so a single node isn't
  // lost in seconds — the player has time to react to the warning.
  for (const n of s.nodes) {
    if (!n.online) continue;
    if (dist(e.x, e.y, n.x, n.y) < e.radius + n.radius) {
      n.stability -= e.def.damage * dmgMul * dt * NODE_DAMAGE_FACTOR;
      n.underAttack = 0.6;
      if (!n.warned && n.stability < 60) {
        n.warned = true;
        s.addFloat(n.x, n.y - n.radius - 8, "UNDER ATTACK", "#e3c78a", 18);
        s.addShake(3);
      }
      if (n.stability <= 0) {
        n.stability = 0;
        n.online = false;
        s.ring(n.x, n.y, "#ff5454", 24, 260);
        s.addFloat(n.x, n.y - n.radius, "NODE DOWN", "#ff5454", 22);
        s.addShake(8);
      }
    }
  }

  // Damage the player on overlap (respecting i-frames).
  if (p.invuln <= 0 && dist(e.x, e.y, p.x, p.y) < e.radius + p.radius) {
    p.integrity -= e.def.damage * dmgMul;
    p.invuln = 0.6;
    s.damageFlash = 1;
    s.addShake(6);
    s.ring(p.x, p.y, "#ff5454", 12, 200);
    s.addFloat(p.x, p.y - p.radius - 6, "-" + Math.round(e.def.damage * dmgMul), "#ff5454", 18);
    if (p.integrity <= 0) {
      p.integrity = 0;
      s.phase = "gameover";
    }
    // Worms are kamikaze: die on contact.
    if (e.def.behavior === "rush") s.killEnemy(e);
    void maxIntegrity; // (kept imported for future shield logic)
  }
}

function nearestFragment(s: GameState, x: number, y: number) {
  let best = null as null | (typeof s.pickups)[number];
  let bestD = Infinity;
  for (const f of s.pickups) {
    if (!f.alive || f.kind !== "fragment") continue;
    const d = dist(x, y, f.x, f.y);
    if (d < bestD) {
      bestD = d;
      best = f;
    }
  }
  return best;
}
